// Internal: rendered exclusively by AccountPane based on account type. Not exported from the panes barrel.
import * as React from 'react';

import BalanceChangeIndicator from '../../components/changeIndicators/BalanceChangeIndicator';
import BalancesChart from '../../components/charts/BalancesChart';
import CurrentBalance from '../../components/CurrentBalance';
import Entity from '../../components/entity';
import AccountPaneHeader from '../../components/headers/AccountPaneHeader';
import { DepositoryAccountSummary } from '../../components/summaries';
import TransactionsTable, { TxnTableElementType } from '../../components/tables/TransactionsTable';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { useDateRangeParams } from '../../providers/DateRangeProvider';

import type { GetFinancialAccountQuery } from './__generated__/AccountPane.gql';

interface IDepositoryAccountPaneProps {
  financialAccount: NonNullable<GetFinancialAccountQuery['getFinancialAccount']>;
}

const DepositoryAccountPane = ({ financialAccount }: IDepositoryAccountPaneProps) => {
  // Date range synced with URL
  const [dateRange, setDateRange] = useDateRangeParams();

  // Filter transactions for balance calculations (includes paired/transfers)
  const { transactions: balanceTransactions, loading: balanceLoading } = useFilteredTransactions({
    filters: [{ key: 'financialAccountId', value: financialAccount.id }],
    includePaired: true,
  });

  // Get current balance for this account
  const currentBalance = React.useMemo(() => {
    if (!financialAccount.currentBalance) {
      return 0;
    }
    return parseFloat(financialAccount.currentBalance.balance);
  }, [financialAccount.currentBalance]);

  const chart = (
    <React.Fragment key="chart">
      <CurrentBalance currentBalance={currentBalance}>
        <BalanceChangeIndicator
          transactions={balanceTransactions}
          currentBalance={currentBalance}
          dateRange={dateRange}
        />
      </CurrentBalance>
      <BalancesChart
        transactions={balanceTransactions}
        currentBalance={currentBalance}
        dateRange={dateRange}
        isLoading={balanceLoading}
      />
    </React.Fragment>
  );

  const table = (
    <TransactionsTable
      key="table"
      financialAccountIds={[financialAccount.id]}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      primaryElement={TxnTableElementType.Merchant}
    />
  );

  const summary = (
    <DepositoryAccountSummary
      transactions={balanceTransactions}
      currentBalance={currentBalance}
      dateRange={dateRange}
    />
  );

  return (
    <Entity.Pane>
      <AccountPaneHeader financialAccount={financialAccount} dateRange={dateRange} onDateRangeChange={setDateRange} />
      <Entity.Body
        leftColumn={[chart, table]}
        rightColumn={summary}
        // Stacked order keeps the summary between the chart and the table.
        singleColumnOrderOverride={[chart, summary, table]}
      />
    </Entity.Pane>
  );
};

export default DepositoryAccountPane;
